import { useState } from 'react';
import { Info } from 'lucide-react';
import { Button, Card, Modal } from './ui';
import { cn } from '../lib/cn';
import { haptic } from '../lib/tma';

const STORAGE_PREFIX = 'masha:how-it-works:';

function readDismissed(id: string): boolean {
  try {
    return localStorage.getItem(STORAGE_PREFIX + id) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(id: string): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + id, '1');
  } catch {
    // localStorage может быть недоступен в WebView; подсказка просто покажется снова
  }
}

export interface HowItWorksProps {
  /** Ключ сервиса; по нему запоминается «Понятно» на устройстве. */
  id: string;
  steps: string[];
  note?: string;
  className?: string;
}

/**
 * Подсказка «Как это работает» для новичка: карточка с шагами и кнопкой
 * «Понятно». После нажатия карточка прячется и на её месте остаётся короткая
 * ссылка, которая открывает тот же текст в модалке. Состояние живёт в
 * localStorage, без сервера.
 */
export function HowItWorks({ id, steps, note, className }: HowItWorksProps) {
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(id));
  const [open, setOpen] = useState(false);

  function dismiss() {
    haptic('light');
    writeDismissed(id);
    setDismissed(true);
    setOpen(false);
  }

  const body = (
    <>
      <ol className="space-y-2 mb-3">
        {steps.map((step, i) => (
          <li key={step} className="flex items-start gap-2.5 text-sm leading-snug">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-muted text-brand text-[11px] font-semibold flex items-center justify-center tabular-nums mt-0.5">
              {i + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      {note && <p className="text-xs text-tg-hint leading-snug mb-3">{note}</p>}
    </>
  );

  if (dismissed) {
    return (
      <div className={className}>
        <button
          type="button"
          onClick={() => {
            haptic('light');
            setOpen(true);
          }}
          className="inline-flex items-center gap-1.5 text-sm text-brand font-medium hover:opacity-80 transition-opacity"
        >
          <Info size={14} />
          Как это работает?
        </button>
        <Modal open={open} onClose={() => setOpen(false)} title="Как это работает">
          {body}
          <Button onClick={dismiss} variant="secondary" size="md" fullWidth>
            Понятно
          </Button>
        </Modal>
      </div>
    );
  }

  return (
    <Card variant="elevated" padding="md" className={cn('', className)}>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-muted text-brand flex items-center justify-center">
          <Info size={16} />
        </div>
        <h2 className="text-sm font-semibold">Как это работает</h2>
      </div>
      {body}
      <Button onClick={dismiss} variant="secondary" size="sm" fullWidth>
        Понятно
      </Button>
    </Card>
  );
}
